import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDeleteUserMutation, useAddUserMutation, locationsApi } from "../../../../../../services/location_services";
import { ReactSelectField } from "../../../../../../components/reactHookFormFields/selectField";
import { TableListing } from "../../../../../../components/AppListing/TableListing";
import { RiDeleteBin6Line } from "react-icons/ri";
import ActionDialog from "../../../../../../components/actionDialog";
import { formatPhoneNumber, getCurrentEpochTime } from "../../../../../../utils/common_lib";
import { CalendarDaysIcon } from '@heroicons/react/20/solid';
import NotificationManagementDialogue from "./NotificationManagementDialogue";
import { useNotifyUserSettingMutation, useUpdateCallSettingMutation } from "../../../../../../services/appointmentService";
import { IoIosArrowDown, IoIosArrowBack } from "react-icons/io";
import MainPageDialog from "../../../../../../components/dialog";
import LocationCheckin from "../../../../../locationCheckin";
import ListIcon from "../../../../../../components/listIcon";
import { IoRemoveCircleOutline } from "react-icons/io5";

function LocationGroupUsers({ providersData = [], currentLocation = {}, locationsData = [] }) {
  const { control, formState: { errors }, setValue, handleSubmit } = useForm();

  const [nursingHomeUsersData, setNursingHomeUsersData] = useState([]);
  const [loadingNursingHomeUsers, setLoadingNursingHomeUsers] = useState(false);
  const [updatingSetting, setUpdatingSetting] = useState(false);
  const [confirmModalState, setConfirmModalState] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showNotificationsManagementDialogue, setShowNotificationsManagementDialogue] = useState({
    show: false,
    user_data: null,
  });
  const [showProviderStaffForm, setShowProviderStaffForm] = useState(true);
  const [isOpenCheckInPopup, setIsOpenCheckInPopup] = useState(false);
  const [addUserTrigger, { isLoading: addingUser }] = useAddUserMutation();
  const [deleteUserTrigger, { isLoading: deletingUser }] = useDeleteUserMutation();
  const [fetchNursingHomeUsersTrigger] = locationsApi.endpoints.getNursingHomeUsers.useLazyQuery();
  const [notifyUserSettingTrigger] = useNotifyUserSettingMutation();
  const [updateCallSettingTrigger] = useUpdateCallSettingMutation();
  const [currentUser, setCurrentUser] = useState(null);

  // Memoized providers options for caching (providersData may be { data: [...] } or array)
  const providersOptions = useMemo(() => {
    const list = providersData?.data || [];
    return list.map((item) => ({
      id: item?.User?.id,
      label: item?.User?.name,
      ...item,
    }));
  }, [providersData]);

  const handleFetchNursingHomeUsersTrigger = useCallback(() => {
    setLoadingNursingHomeUsers(true);
    try {
      fetchNursingHomeUsersTrigger({
        _dc: getCurrentEpochTime().milliseconds,
        location_id: currentLocation?.id,
        page: 1,
        start: 0,
        limit: 200,
      })
        .then((res) => {
          if (res?.data?.data) {
            setNursingHomeUsersData(res?.data?.data);
          } else {
            setNursingHomeUsersData([]);
          }
        })
        .catch((err) => {
          setNursingHomeUsersData([]);
          console.log("Error in api fetching nursing home users", err);
          toast.error("Error in api fetching nursing home users");
        })
        .finally(() => {
          setLoadingNursingHomeUsers(false);
        });
    } catch (error) {
      console.log("Error calling fetching nursing home users", error);
      toast.error("Error calling fetching nursing home users");
    }
  }, [fetchNursingHomeUsersTrigger, currentLocation?.id]);

  const handleNotifyUserSetting = useCallback(async (data) => {
    try {
      setUpdatingSetting(true);
      await notifyUserSettingTrigger(data)
        .then((res) => {
          const { code, msg } = res?.data;
          if (code === 0 || code === "0") {
            toast.success(msg || "User settings updated successfully");
          } else {
            if (res?.data?.code === -1) {
              // toast.error(res?.data?.msg);
            } else {
              toast.error("Failed to update user settings");
            }
          }
        })
        .catch((err) => {
          console.log("Error in updating notify user setting", err);
          toast.error("Error in updating notify user setting");
        })
        .finally(() => {
          setUpdatingSetting(false);
          handleFetchNursingHomeUsersTrigger();
        });
    } catch (error) {
      console.log("Error while updating notify user setting", error);
      toast.error("Error while updating notify user setting");
    }
  }, [notifyUserSettingTrigger, handleFetchNursingHomeUsersTrigger]);

  const handleUpdateCallSetting = useCallback(async (data) => {
    try {
      setUpdatingSetting(true);
      await updateCallSettingTrigger(data)
        .then((res) => {
          const { code, msg } = res?.data;
          if (code === 0 || code === "0") {
            toast.success(msg || "User settings updated successfully");
          } else {
            if (res?.data?.code === -1) {
              // toast.error(res?.data?.msg);
            } else {
              toast.error("Failed to update user settings");
            }
          }
        })
        .catch((err) => {
          console.log("Error in updating notify user setting", err);
          toast.error("Error in updating notify user setting");
        })
        .finally(() => {
          setUpdatingSetting(false);
          handleFetchNursingHomeUsersTrigger();
        });
    } catch (error) {
      console.log("Error while updating notify user setting", error);
      toast.error("Error while updating notify user setting");
    }
  }, [updateCallSettingTrigger, handleFetchNursingHomeUsersTrigger]);

  useEffect(() => {
    handleFetchNursingHomeUsersTrigger();
  }, [handleFetchNursingHomeUsersTrigger]);

  const handleSaveUser = useCallback(async (data) => {
    if (!data?.new_user) {
      toast.error("Please provide user");
      return;
    }

    try {
      const payload = {
        body: {
          location_id: currentLocation?.id,
          user_id: data?.new_user?.id,
        },
      };

      addUserTrigger(payload)
        .then((res) => {
          const { code, data, msg } = res?.data;
          if (code === 0 || code === "0") {
            toast.success(msg || "User added successfully");
            handleFetchNursingHomeUsersTrigger();
          }
        })
        .catch((err) => {
          console.log("Error adding user", err);
          toast.error("Error adding user");
        })
        .finally(() => {
          setValue("new_user", null);
        });
    } catch (err) {
      console.log("Error while adding user");
      toast.error("Error while adding user");
    }
  }, [addUserTrigger, currentLocation?.id, setValue]);

  const handleRemoveUser = useCallback((item) => {
    if (deletingUser || addingUser) return;
    setConfirmModalState(true);
    setSelectedUser(item);
  }, [addingUser, deletingUser]);

  const onCancelHandler = useCallback(() => {
    setConfirmModalState(false);
    setSelectedUser(null);
  }, []);

  const onConfirmHandler = useCallback(() => {
    try {
      const payload = {
        body: {
          location_id: currentLocation?.id,
          user_id: selectedUser?.user_id,
        },
      };

      deleteUserTrigger(payload)
        .then((res) => {
          const { code } = res?.data;
          if (code === 0 || code === "0") {
            toast.success("User removed successfully");
            handleFetchNursingHomeUsersTrigger();
          }
        })
        .catch((err) => {
          console.log("Error in delete user", err);
          toast.error("Error in delete user");
        })
        .finally(() => {
          onCancelHandler();
        });
    } catch (err) {
      console.log("Error calling delete user");
      toast.error("Error calling delete user");
    }
  }, [deleteUserTrigger, currentLocation?.id, onCancelHandler, selectedUser]);

  const handleCheckboxChange = useCallback((item, col, checked) => {
    try {
      const updatedData = nursingHomeUsersData.map((row) => {
        // Assuming each row has a unique user_id to identify it
        if (row.user_id === item.user_id) {
          return { ...row, [col.key]: checked };
        }
        return row;
      });
      setNursingHomeUsersData(updatedData);
    } catch (error) {
      console.error("Error updating checkbox value:", error);
    }
  }, [nursingHomeUsersData]);

  const cols = useMemo(() => [
    { title: "Name", key: "name", colClass: "flex text-left",
      getData: (item) => item?.name || "--"
    },
    {
      title: "Phone",
      key: "user.phone",
      colClass: "flex text-left",
      getData: (item) =>
        item?.user?.phonecell ? formatPhoneNumber(item?.user?.phonecell) : "--",
    },
    {
      title: "NPI",
      key: "user.npi",
      colClass: "flex text-left",
      getData: (item) => item?.user?.npi || "--",
    },
    {
      title: "Active",
      key: "active",
      colClass: "flex text-left",
      getData: (item) => (item?.active ? "Yes" : "No"),
    },
    // {
    //   title: "Text",
    //   key: "notify_via_sms",
    //   colClass: "flex text-left",
    //   checkbox: true,
    //   disabled: updatingSetting || loadingNursingHomeUsers || false,
    //   onChange: (checked, item) => {
    //     handleNotifyUserSetting({
    //       user_id: item?.user_id,
    //       notify_via_sms: checked ? 1 : 0,
    //       id: item?.location_id   
    //     });
    //   }
    // },
    // {
    //   title: "Phone Call",
    //   key: "call_phone",
    //   colClass: "flex text-left",
    //   checkbox: true,
    //   disabled: updatingSetting || loadingNursingHomeUsers || false,
    //   onChange: (checked, item) => {
    //     handleUpdateCallSetting({
    //       user_id: item?.user_id,
    //       flag: "call_phone",
    //       value: checked ? "allow" : "not_allow",
    //       location_id: item?.location_id   
    //     });
    //   }
    // },
    // {
    //   title: "ethizo Call",
    //   key: "call_in_app",
    //   colClass: "flex text-left",
    //   checkbox: true,
    //   disabled: updatingSetting || loadingNursingHomeUsers || false,
    //   onChange: (checked, item) => {
    //     handleUpdateCallSetting({
    //       user_id: item?.user_id,
    //       flag: "call_in_app",
    //       value: checked ? "allow" : "not_allow",
    //       location_id: item?.location_id   
    //     });
    //   }
    // },
    {
      title: "Actions",
      colClass: "flex text-left",
      skipColHtmlTooltip: true,
      getData: (item) => {
        return (
          <div className="flex gap-2 items-center">
            <span
              title="Notification Management"
              className="cursor-pointer"
              onClick={() => setShowNotificationsManagementDialogue({
                show: true,
                user_data: item,
              })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setShowNotificationsManagementDialogue({
                    show: true,
                    user_data: item,
                  });
                }
              }}
            >
              <CalendarDaysIcon className="h-4 w-4 text-[#00acc1] cursor-pointer" />
            </span>
            <ListIcon
              title="Check In/Out Locations"
              handler={() => {
                setCurrentUser(item);
                setIsOpenCheckInPopup(true);
              }}
              type="check-in"
              disabled={false}
              color="#00acc1"
            />
             <span
              title="Remove"
              className="cursor-pointer"
              onClick={() => handleRemoveUser(item)}
              onKeyDown={() => handleRemoveUser(item)}
            >
              <IoRemoveCircleOutline color="#fc0505" />
            </span>
          </div>
        );
      },
    },
  ], [nursingHomeUsersData, updatingSetting, loadingNursingHomeUsers, handleNotifyUserSetting, handleUpdateCallSetting, handleRemoveUser, showNotificationsManagementDialogue]);

  return (
    <>
      {confirmModalState && (
        <ActionDialog
          onCancelHandler={onCancelHandler}
          setShowModal={onCancelHandler}
          onConfirmHandler={onConfirmHandler}
          showModal={confirmModalState}
          title="Are you sure you want to remove the user from Nursing Chat of this location?"
          headerTitle="Remove User"
        />
      )}

      {showNotificationsManagementDialogue?.show ? 
        <NotificationManagementDialogue 
          setShowNotificationsManagementDialogue={setShowNotificationsManagementDialogue}
          showNotificationsManagementDialogue={showNotificationsManagementDialogue || {}}
          currentLocation={currentLocation || {}}
          locationsData={locationsData || []}
          path="careco"
        /> 
      : null}

      {isOpenCheckInPopup ?
        <MainPageDialog
          title={`New Check In or Check Out (${currentUser?.name || ""})`}
          onCancelHandler={() => {
            setIsOpenCheckInPopup(false);
            setCurrentUser(null);
          }}
          component={
            <LocationCheckin
              closeLocationApptPopup={() => {
                setIsOpenCheckInPopup(false);
                setCurrentUser(null);
              }}
              currentUser={currentUser}
              currentLocation={currentLocation}
              refetchData={handleFetchNursingHomeUsersTrigger}
            />
          }
          footer={false}
        />
        : null
      }

    <div className="flex flex-col gap-2 border-2 border-gray-100 rounded-md bg-slate-50 p-2 mt-4">
      <div className="p-2 bg-bgGreen text-white shadow-md flex items-center justify-between cursor-pointer" onClick={() => setShowProviderStaffForm(!showProviderStaffForm)}>
        <p className="text-left">Practice Staff</p>
        {showProviderStaffForm ? <IoIosArrowDown /> : <IoIosArrowBack />}
      </div>
      {showProviderStaffForm ? 
        <>
          <p className="text-left font-bold mt-2">Add Existing Provider/User:</p>
          <form onSubmit={handleSubmit(handleSaveUser)}>
            <div className="grid grid-cols-12 gap-4 mt-4">
              <div className="col-span-6 md:col-span-2">
                <ReactSelectField
                  name="new_user"
                  control={control}
                  errors={errors}
                  title="Select User:"
                  menuPlacement="bottom"
                  options={providersOptions}
                  onChange={(e) => setValue("new_user", e)}
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    aria-label="Save"
                    title="Save"
                    disabled={addingUser || deletingUser || loadingNursingHomeUsers}
                    className="flex gap-1 items-center bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 border text-md"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </form>
          <div className="mt-5 max-h-[400px] overflow-y-auto overflow-x-hidden w-[360px] md:w-auto">
            <TableListing
              listData={nursingHomeUsersData || []}
              cols={cols || []}
              pagination={false}
              onCheckboxChange={handleCheckboxChange}
            />
          </div>
        </>
      : null}
    </div>
    </>
  );
}

export default LocationGroupUsers;
